/* Ordinary collection cards only. Native scrolling remains the gallery engine. */
(() => {
  'use strict';
  if (window.__corsailleGridGalleryV4) return;
  window.__corsailleGridGalleryV4 = true;
  const cards = new Map(), queue = new Set(), products = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let seen = 0, active = null, hintTimer = 0, cooldown = 0, scanFrame = 0;
  const now = () => performance.now();
  const plain = e => !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
  const listen = (s, node, type, fn, options) => {
    node.addEventListener(type, fn, options);
    s.off.push(() => node.removeEventListener(type, fn, options));
  };
  function hydrate(s, index) {
    const img = s.slides[index]?.querySelector('img');
    if (!img) return null;
    if (img.dataset.cv4Srcset) {
      img.srcset = img.dataset.cv4Srcset;
      img.removeAttribute('data-cv4-srcset');
    }
    if (img.dataset.cv4Src) {
      img.loading = 'eager';
      img.src = img.dataset.cv4Src;
      img.removeAttribute('data-cv4-src');
    }
    return img;
  }
  function sync(s) {
    const width = s.track.clientWidth;
    if (!width) return;
    s.index = Math.max(0, Math.min(s.slides.length - 1, Math.round(s.track.scrollLeft / width)));
    s.dots.forEach(dot => dot.setAttribute('aria-pressed', String(Number(dot.dataset.cv4Dot) === s.index)));
  }
  const eligible = s => s.card.isConnected && s.ratio >= .65 && !s.memory.interacted &&
    !s.memory.revealed && !document.hidden && !motion.matches && s.track.scrollLeft < 3 && s.track.clientWidth > 0;
  function finishHint(s) {
    if (active !== s) return;
    cancelAnimationFrame(s.hintFrame);
    s.track.style.scrollSnapType = s.snap;
    s.track.style.scrollBehavior = s.behavior;
    active = null;
    cooldown = now() + 3500;
    sync(s);
    scheduleHint();
  }
  function interact(s) {
    s.memory.interacted = true;
    queue.delete(s);
    finishHint(s);
  }
  function startHint(s) {
    queue.delete(s);
    if (!eligible(s)) return scheduleHint();
    s.memory.revealed = true; // One attempt only; never retry an unloaded image or an interrupted hint.
    const img = hydrate(s, 1);
    if (!img?.complete || !img.naturalWidth) return scheduleHint();
    active = s;
    s.snap = s.track.style.scrollSnapType;
    s.behavior = s.track.style.scrollBehavior;
    s.track.style.scrollSnapType = 'none';
    s.track.style.scrollBehavior = 'auto';
    const started = now(), distance = s.track.clientWidth * .22;
    const ease = t => .5 - Math.cos(Math.PI * Math.max(0, Math.min(1, t))) / 2;
    function frame() {
      if (!s.card.isConnected || s.ratio < .65 || s.memory.interacted || document.hidden || motion.matches) return finishHint(s);
      const elapsed = now() - started;
      const progress = elapsed < 450 ? ease(elapsed / 450) : elapsed < 530 ? 1 : 1 - ease((elapsed - 530) / 570);
      s.track.scrollLeft = distance * progress;
      if (elapsed < 1100) s.hintFrame = requestAnimationFrame(frame);
      else { s.track.scrollLeft = 0; finishHint(s); }
    }
    s.hintFrame = requestAnimationFrame(frame);
  }
  function scheduleHint() {
    clearTimeout(hintTimer);
    hintTimer = 0;
    if (active || document.hidden || motion.matches) return;
    for (const s of queue) if (!eligible(s)) queue.delete(s);
    const next = [...queue].sort((a, b) => a.due - b.due)[0];
    if (next) hintTimer = setTimeout(() => startHint(next), Math.max(0, next.due - now(), cooldown - now()));
  }
  function go(s, index) {
    interact(s);
    index = Math.max(0, Math.min(s.slides.length - 1, index));
    hydrate(s, index);
    s.track.scrollTo({ left: index * s.track.clientWidth, behavior: motion.matches ? 'auto' : 'smooth' });
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const s = cards.get(entry.target);
      if (!s) return;
      s.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
      if (s.ratio >= .65 && !document.hidden) {
        hydrate(s, 1);
        if (!s.memory.seen) {
          s.memory.seen = true;
          if (++seen % 7 === 0 && eligible(s)) { s.due = now() + 650; queue.add(s); }
        }
      } else { queue.delete(s); finishHint(s); }
    });
    scheduleHint();
  }, { threshold: [0, .65] }) : null;
  function mount(card) {
    if (cards.has(card)) return;
    const gallery = card.querySelector('[data-cv4-gallery]');
    const track = gallery?.querySelector('[data-cv4-track]');
    const slides = track ? [...track.querySelectorAll('[data-cv4-slide]')].slice(0, 3) : [];
    if (slides.length < 2) {
      if (gallery) delete gallery.dataset.cv4Ready;
      const dots = gallery?.querySelector('[data-cv4-dots]');
      if (dots) dots.hidden = true;
      return;
    }
    const productId = card.dataset.productId;
    const memory = (productId && products.get(productId)) || { seen: false, interacted: false, revealed: false };
    if (card.dataset.cv4Interacted === 'true') memory.interacted = true;
    if (productId) products.set(productId, memory);
    const dotsContainer = gallery.querySelector('[data-cv4-dots]');
    gallery.dataset.cv4Ready = 'true';
    if (dotsContainer) dotsContainer.hidden = false;
    const s = { card, track, slides, dots: [...gallery.querySelectorAll('[data-cv4-dots] button[data-cv4-dot]')],
      memory, off: [], ratio: 0, index: 0, gesture: null, suppressUntil: 0 };
    cards.set(card, s);
    listen(s, card, 'cs:gallery-interact', () => interact(s));
    s.dots.forEach(dot => listen(s, dot, 'click', e => {
      e.preventDefault();
      e.stopPropagation();
      go(s, Number(dot.dataset.cv4Dot) || 0);
    }));
    listen(s, track, 'pointerdown', e => {
      if (e.button !== 0 || !plain(e) || e.isPrimary === false) return;
      interact(s);
      s.suppressUntil = 0;
      s.gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, left: track.scrollLeft, at: now(), drag: false };
      slides.forEach((_, index) => hydrate(s, index));
    }, { passive: true });
    listen(s, track, 'pointermove', e => {
      const g = s.gesture;
      if (!g || g.id !== e.pointerId) return;
      const dx = Math.abs(e.clientX - g.x), dy = Math.abs(e.clientY - g.y);
      if (dx > 10 && dx > dy * 1.15) { g.drag = true; s.suppressUntil = now() + 450; }
    }, { passive: true });
    const end = e => {
      const g = s.gesture;
      if (!g || g.id !== e.pointerId) return;
      if (g.drag || Math.abs(track.scrollLeft - g.left) > 8) s.suppressUntil = now() + 450;
      // Keep the short-lived gesture: native touch scrolling may start after pointercancel.
      g.at = now();
    };
    listen(s, track, 'pointerup', end, { passive: true });
    listen(s, track, 'pointercancel', end, { passive: true });
    listen(s, track, 'click', e => {
      if (e.detail !== 0 && (e.button === 0 || e.button == null) && plain(e) && now() < s.suppressUntil) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    listen(s, track, 'scroll', () => {
      const g = s.gesture;
      if (g && now() - g.at < 1000 && Math.abs(track.scrollLeft - g.left) > 8) s.suppressUntil = now() + 450;
      if (!s.scrollFrame) s.scrollFrame = requestAnimationFrame(() => { s.scrollFrame = 0; sync(s); });
    }, { passive: true });
    listen(s, track, 'keydown', e => {
      if (!plain(e) || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();
      sync(s);
      go(s, s.index + (e.key === 'ArrowRight' ? 1 : -1));
    });
    listen(s, track, 'focusin', () => interact(s));
    listen(s, track, 'wheel', () => { interact(s); slides.forEach((_, index) => hydrate(s, index)); }, { passive: true });
    sync(s);
    observer?.observe(card);
  }
  function scan() {
    scanFrame = 0;
    for (const [card, s] of cards) if (!card.isConnected || !s.track.isConnected || card.querySelector('[data-cv4-track]') !== s.track) {
      queue.delete(s);
      finishHint(s);
      cancelAnimationFrame(s.scrollFrame);
      observer?.unobserve(card);
      s.off.forEach(off => off());
      cards.delete(card);
    }
    document.querySelectorAll('article[data-cv4-card]').forEach(mount);
  }
  function init() {
    scan();
    new MutationObserver(() => {
      if (!scanFrame) scanFrame = requestAnimationFrame(scan);
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && active) finishHint(active);
      scheduleHint();
    });
    const motionChange = () => { if (motion.matches && active) finishHint(active); scheduleHint(); };
    if (motion.addEventListener) motion.addEventListener('change', motionChange);
    else motion.addListener?.(motionChange);
    window.addEventListener('resize', () => cards.forEach(sync), { passive: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
